import React, { useEffect, useState, useRef } from 'react';
import { MapPin, Building2, TrendingUp, Users, Target, CheckCircle, AlertTriangle, BarChart3, Loader2, Search } from 'lucide-react';
interface AnalysisResult {
  street: string;
  client: string;
  companies: CompanyInArea[];
  compatibility: CompatibilityScore;
  competition: CompetitionAnalysis;
  footTraffic: FootTrafficData;
  recommendations: string[];
}
interface CompanyInArea {
  name: string;
  category: string;
  distance: string;
  relevance: 'high' | 'medium' | 'low';
}
interface CompatibilityScore {
  overall: number;
  factors: {
    name: string;
    score: number;
    description: string;
  }[];
}
interface CompetitionAnalysis {
  level: 'high' | 'medium' | 'low';
  directCompetitors: number;
  marketSaturation: number;
  opportunities: string[];
  threats: string[];
}
interface FootTrafficData {
  dailyAverage: number;
  peakHours: string[];
  demographics: {
    label: string;
    percentage: number;
  }[];
  trend: 'increasing' | 'stable' | 'decreasing';
}
const mockStreets = [{
  address: 'Via Montenapoleone',
  city: 'Milano',
  cap: '20121'
}, {
  address: 'Via della Spiga',
  city: 'Milano',
  cap: '20121'
}, {
  address: 'Corso Vittorio Emanuele II',
  city: 'Milano',
  cap: '20122'
}, {
  address: 'Via Torino',
  city: 'Milano',
  cap: '20123'
}, {
  address: 'Corso Buenos Aires',
  city: 'Milano',
  cap: '20124'
}, {
  address: 'Via Roma',
  city: 'Roma',
  cap: '00186'
}, {
  address: 'Via del Corso',
  city: 'Roma',
  cap: '00187'
}, {
  address: 'Via Condotti',
  city: 'Roma',
  cap: '00187'
}];
const mockClients = [{
  id: '1',
  name: 'Luxury Fashion Brand',
  category: 'Fashion'
}, {
  id: '2',
  name: 'Tech Startup Store',
  category: 'Technology'
}, {
  id: '3',
  name: 'Gourmet Restaurant',
  category: 'Food & Beverage'
}, {
  id: '4',
  name: 'Fitness Center',
  category: 'Health & Wellness'
}];
export function StreetAnalysis() {
  const [streetInput, setStreetInput] = useState('');
  const [selectedStreet, setSelectedStreet] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredStreets, setFilteredStreets] = useState(mockStreets);
  const [selectedClient, setSelectedClient] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleStreetInputChange = (value: string) => {
    setStreetInput(value);
    setSelectedStreet('');
    if (value.trim()) {
      const filtered = mockStreets.filter(street => street.address.toLowerCase().includes(value.toLowerCase()) || street.city.toLowerCase().includes(value.toLowerCase()) || street.cap.includes(value));
      setFilteredStreets(filtered);
      setShowSuggestions(true);
    } else {
      setFilteredStreets(mockStreets);
      setShowSuggestions(false);
    }
  };
  const handleStreetSelect = (street: (typeof mockStreets)[0]) => {
    const fullAddress = `${street.address}, ${street.city} ${street.cap}`;
    setStreetInput(fullAddress);
    setSelectedStreet(fullAddress);
    setShowSuggestions(false);
  };
  const handleAnalysis = () => {
    if (!selectedStreet || !selectedClient) return;
    setIsAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      const mockResult: AnalysisResult = {
        street: selectedStreet,
        client: selectedClient,
        companies: [{
          name: 'Gucci',
          category: 'Fashion Luxury',
          distance: '50m',
          relevance: 'high'
        }, {
          name: 'Prada',
          category: 'Fashion Luxury',
          distance: '120m',
          relevance: 'high'
        }, {
          name: 'Apple Store',
          category: 'Technology',
          distance: '200m',
          relevance: 'medium'
        }, {
          name: 'Starbucks',
          category: 'Food & Beverage',
          distance: '80m',
          relevance: 'low'
        }],
        compatibility: {
          overall: 87,
          factors: [{
            name: 'Target Demografico',
            score: 92,
            description: 'Alta presenza del target di riferimento'
          }, {
            name: 'Brand Positioning',
            score: 88,
            description: 'Coerente con il posizionamento luxury'
          }, {
            name: 'Accessibilità',
            score: 85,
            description: 'Ottima accessibilità con mezzi pubblici'
          }, {
            name: 'Visibilità',
            score: 82,
            description: 'Buona visibilità dalla strada principale'
          }]
        },
        competition: {
          level: 'medium',
          directCompetitors: 3,
          marketSaturation: 65,
          opportunities: ['Spazio disponibile in zona ad alto traffico', 'Mancanza di concept store innovativi', 'Crescente interesse per esperienze retail premium'],
          threats: ['Presenza consolidata di brand competitor', 'Costi di locazione elevati', 'Alta aspettativa dei clienti sulla qualità']
        },
        footTraffic: {
          dailyAverage: 15000,
          peakHours: ['10:00-12:00', '15:00-19:00'],
          demographics: [{
            label: 'Alto Reddito',
            percentage: 45
          }, {
            label: 'Turisti',
            percentage: 30
          }, {
            label: 'Professionisti',
            percentage: 15
          }, {
            label: 'Residenti',
            percentage: 10
          }],
          trend: 'increasing'
        },
        recommendations: ['Location ideale per brand luxury con target alto-spendente', 'Considerare vetrine ampie per massimizzare la visibilità', 'Investire in experience retail per differenziarsi dalla concorrenza', 'Pianificare apertura in periodo pre-stagionale per massimizzare impatto']
      };
      setAnalysisResult(mockResult);
      setIsAnalyzing(false);
    }, 2000);
  };
  const getRelevanceColor = (relevance: string) => {
    switch (relevance) {
      case 'high':
        return 'text-green-600 bg-green-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'low':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'high':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };
  return <div className="p-6 max-w-7xl mx-auto" data-id="element-3698">
      <div className="mb-6" data-id="element-3699">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3700">Street Analysis</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-3701">
          Analisi AI per screening preventivo della location
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" data-id="element-3702">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3703">
          <div className="relative" data-id="element-3704">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3705">
              Cerca Strada
            </label>
            <div className="relative" data-id="element-3706">
              <input ref={inputRef} type="text" value={streetInput} onChange={e => handleStreetInputChange(e.target.value)} onFocus={() => setShowSuggestions(true)} placeholder="Inserisci via, città o CAP..." className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1388ad] focus:border-transparent" data-id="element-3707" />
              <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3708" />
            </div>
            {showSuggestions && filteredStreets.length > 0 && <div ref={dropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-id="element-3709">
                {filteredStreets.map((street, index) => <button key={index} onClick={() => handleStreetSelect(street)} className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0" data-id="element-3710">
                    <div className="flex items-center gap-3" data-id="element-3711">
                      <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-3712" />
                      <div data-id="element-3713">
                        <div className="text-sm font-medium text-gray-900" data-id="element-3714">
                          {street.address}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-3715">
                          {street.city} - {street.cap}
                        </div>
                      </div>
                    </div>
                  </button>)}
              </div>}
          </div>
          <div data-id="element-3716">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3717">
              Seleziona Cliente
            </label>
            <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1388ad] focus:border-transparent" data-id="element-3718">
              <option value="" data-id="element-3719">Scegli un cliente...</option>
              {mockClients.map(client => <option key={client.id} value={client.name} data-id="element-3720">
                  {client.name} - {client.category}
                </option>)}
            </select>
          </div>
        </div>
        <button onClick={handleAnalysis} disabled={!selectedStreet || !selectedClient || isAnalyzing} className="w-full bg-[#1388ad] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#0f7090] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-3721">
          {isAnalyzing ? <>
              <Loader2 className="w-5 h-5 animate-spin" data-id="element-3722" />
              Analisi in corso...
            </> : <>
              <Search className="w-5 h-5" data-id="element-3723" />
              Avvia Analisi AI
            </>}
        </button>
      </div>
      {analysisResult && <div className="space-y-6" data-id="element-3724">
          {/* Compatibility Score */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3725">
            <div className="flex items-center gap-3 mb-4" data-id="element-3726">
              <div className="w-10 h-10 bg-[#1388ad] bg-opacity-10 rounded-lg flex items-center justify-center" data-id="element-3727">
                <Target className="w-5 h-5 text-[#1388ad]" data-id="element-3728" />
              </div>
              <div data-id="element-3729">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3730">
                  Compatibilità Location
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3731">
                  Score complessivo di compatibilità
                </p>
              </div>
            </div>
            <div className="mb-6" data-id="element-3732">
              <div className="flex items-center justify-between mb-2" data-id="element-3733">
                <span className="text-sm font-medium text-gray-700" data-id="element-3734">
                  Score Complessivo
                </span>
                <span className="text-2xl font-bold text-[#1388ad]" data-id="element-3735">
                  {analysisResult.compatibility.overall}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3" data-id="element-3736">
                <div className="bg-[#1388ad] h-3 rounded-full transition-all duration-500" style={{
              width: `${analysisResult.compatibility.overall}%`
            }} data-id="element-3737" />
              </div>
            </div>
            <div className="space-y-3" data-id="element-3738">
              {analysisResult.compatibility.factors.map((factor, index) => <div key={index} className="border border-gray-200 rounded-lg p-4" data-id="element-3739">
                  <div className="flex items-center justify-between mb-2" data-id="element-3740">
                    <span className="text-sm font-medium text-gray-900" data-id="element-3741">
                      {factor.name}
                    </span>
                    <span className="text-sm font-semibold text-[#1388ad]" data-id="element-3742">
                      {factor.score}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-600" data-id="element-3743">{factor.description}</p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2" data-id="element-3744">
                    <div className="bg-[#1388ad] h-1.5 rounded-full" style={{
                width: `${factor.score}%`
              }} data-id="element-3745" />
                  </div>
                </div>)}
            </div>
          </div>
          {/* Companies in Area */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3746">
            <div className="flex items-center gap-3 mb-4" data-id="element-3747">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-3748">
                <Building2 className="w-5 h-5 text-purple-600" data-id="element-3749" />
              </div>
              <div data-id="element-3750">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3751">
                  Aziende nell'Area
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3752">
                  Brand e attività nelle vicinanze
                </p>
              </div>
            </div>
            <div className="space-y-3" data-id="element-3753">
              {analysisResult.companies.map((company, index) => <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3754">
                  <div className="flex-1" data-id="element-3755">
                    <div className="flex items-center gap-3" data-id="element-3756">
                      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-3757">
                        <Building2 className="w-4 h-4 text-gray-600" data-id="element-3758" />
                      </div>
                      <div data-id="element-3759">
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-3760">
                          {company.name}
                        </h3>
                        <p className="text-xs text-gray-600" data-id="element-3761">
                          {company.category}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3762">
                    <span className="text-xs text-gray-600" data-id="element-3763">
                      {company.distance}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRelevanceColor(company.relevance)}`} data-id="element-3764">
                      {company.relevance === 'high' ? 'Alta' : company.relevance === 'medium' ? 'Media' : 'Bassa'}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Competition Analysis */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3765">
            <div className="flex items-center gap-3 mb-4" data-id="element-3766">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center" data-id="element-3767">
                <TrendingUp className="w-5 h-5 text-orange-600" data-id="element-3768" />
              </div>
              <div data-id="element-3769">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3770">
                  Analisi Competitività
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3771">
                  Livello di competizione e saturazione del mercato
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-3772">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-3773">
                <div className="text-xs text-gray-600 mb-1" data-id="element-3774">
                  Livello Competizione
                </div>
                <div className={`text-2xl font-bold ${getCompetitionColor(analysisResult.competition.level)}`} data-id="element-3775">
                  {analysisResult.competition.level === 'high' ? 'Alta' : analysisResult.competition.level === 'medium' ? 'Media' : 'Bassa'}
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-3776">
                <div className="text-xs text-gray-600 mb-1" data-id="element-3777">
                  Competitor Diretti
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-3778">
                  {analysisResult.competition.directCompetitors}
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-3779">
                <div className="text-xs text-gray-600 mb-1" data-id="element-3780">
                  Saturazione Mercato
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-3781">
                  {analysisResult.competition.marketSaturation}%
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3782">
              <div data-id="element-3783">
                <div className="flex items-center gap-2 mb-3" data-id="element-3784">
                  <CheckCircle className="w-4 h-4 text-green-600" data-id="element-3785" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3786">
                    Opportunità
                  </h3>
                </div>
                <ul className="space-y-2" data-id="element-3787">
                  {analysisResult.competition.opportunities.map((opp, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3788">
                        <span className="text-green-600 mt-0.5" data-id="element-3789">•</span>
                        <span data-id="element-3790">{opp}</span>
                      </li>)}
                </ul>
              </div>
              <div data-id="element-3791">
                <div className="flex items-center gap-2 mb-3" data-id="element-3792">
                  <AlertTriangle className="w-4 h-4 text-orange-600" data-id="element-3793" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-3794">
                    Minacce
                  </h3>
                </div>
                <ul className="space-y-2" data-id="element-3795">
                  {analysisResult.competition.threats.map((threat, index) => <li key={index} className="text-xs text-gray-600 flex items-start gap-2" data-id="element-3796">
                      <span className="text-orange-600 mt-0.5" data-id="element-3797">•</span>
                      <span data-id="element-3798">{threat}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
          {/* Foot Traffic */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3799">
            <div className="flex items-center gap-3 mb-4" data-id="element-3800">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-3801">
                <Users className="w-5 h-5 text-blue-600" data-id="element-3802" />
              </div>
              <div data-id="element-3803">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3804">
                  Analisi Flussi Pedonali
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3805">
                  Traffico e demografia dell'area
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-3806">
              <div data-id="element-3807">
                <div className="border border-gray-200 rounded-lg p-4 mb-4" data-id="element-3808">
                  <div className="text-xs text-gray-600 mb-1" data-id="element-3809">
                    Media Giornaliera
                  </div>
                  <div className="text-3xl font-bold text-gray-900" data-id="element-3810">
                    {analysisResult.footTraffic.dailyAverage.toLocaleString()}
                  </div>
                  <div className="flex items-center gap-1 mt-2" data-id="element-3811">
                    <TrendingUp className="w-4 h-4 text-green-600" data-id="element-3812" />
                    <span className="text-xs text-green-600 font-medium" data-id="element-3813">
                      In crescita
                    </span>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-3814">
                  <div className="text-xs text-gray-600 mb-3" data-id="element-3815">
                    Orari di Picco
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3816">
                    {analysisResult.footTraffic.peakHours.map((hour, index) => <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-3817">
                        {hour}
                      </span>)}
                  </div>
                </div>
              </div>
              <div data-id="element-3818">
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-3819">
                  <div className="text-xs text-gray-600 mb-3" data-id="element-3820">
                    Composizione Demografica
                  </div>
                  <div className="space-y-3" data-id="element-3821">
                    {analysisResult.footTraffic.demographics.map((demo, index) => <div key={index} data-id="element-3822">
                          <div className="flex items-center justify-between mb-1" data-id="element-3823">
                            <span className="text-xs font-medium text-gray-700" data-id="element-3824">
                              {demo.label}
                            </span>
                            <span className="text-xs font-semibold text-gray-900" data-id="element-3825">
                              {demo.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-3826">
                            <div className="bg-blue-600 h-2 rounded-full" style={{
                      width: `${demo.percentage}%`
                    }} data-id="element-3827" />
                          </div>
                        </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Recommendations */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3828">
            <div className="flex items-center gap-3 mb-4" data-id="element-3829">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-3830">
                <BarChart3 className="w-5 h-5 text-green-600" data-id="element-3831" />
              </div>
              <div data-id="element-3832">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-3833">
                  Raccomandazioni AI
                </h2>
                <p className="text-sm text-gray-600" data-id="element-3834">
                  Suggerimenti per ottimizzare la vendita
                </p>
              </div>
            </div>
            <div className="space-y-3" data-id="element-3835">
              {analysisResult.recommendations.map((rec, index) => <div key={index} className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-3836">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-3837" />
                  <p className="text-sm text-gray-700" data-id="element-3838">{rec}</p>
                </div>)}
            </div>
          </div>
        </div>}
    </div>;
}